import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Eye, EyeOff, KeyRound } from 'lucide-react';
import { toast } from 'sonner';
import { PublicHeader } from '@/components/PublicHeader';

export default function ResetPassword() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [hasValidSession, setHasValidSession] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Check if we have a valid recovery session
    const checkSession = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      
      // Check if this is a recovery session (user clicked email link)
      if (session?.user) {
        setHasValidSession(true);
      } else {
        // No valid session, redirect to auth
        navigate('/auth', { replace: true });
      }
      setIsLoading(false);
    };

    // Listen for auth state changes (recovery link clicked)
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (event === 'PASSWORD_RECOVERY') {
          setHasValidSession(true);
          setIsLoading(false);
        } else if (event === 'SIGNED_IN' && !hasValidSession) {
          // User is signed in but not through recovery, redirect
          navigate('/', { replace: true });
        }
      }
    );

    checkSession();

    return () => subscription.unsubscribe();
  }, [navigate, hasValidSession]);

  // Check if password meets the displayed requirements (8+ chars, lowercase, uppercase, numbers)
  const passwordMeetsRequirements = password.length >= 8 && 
    /[a-z]/.test(password) && 
    /[A-Z]/.test(password) && 
    /[0-9]/.test(password);

  const passwordsMatch = password === confirmPassword;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!passwordMeetsRequirements) {
      toast.error(t('authPasswordNotMeet'));
      return;
    }

    if (!passwordsMatch) {
      toast.error(t('resetPasswordMismatch'));
      return;
    }

    setIsSubmitting(true);

    try {
      const { error } = await supabase.auth.updateUser({
        password: password
      });

      if (error) {
        if (import.meta.env.DEV) {
          console.error('Password update error:', error.message);
        }
        toast.error(t('resetPasswordError'));
      } else {
        toast.success(t('resetPasswordSuccess'));
        // Redirect to main app after successful password reset
        navigate('/', { replace: true });
      }
    } catch {
      toast.error(t('authUnexpectedError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen-safe flex items-center justify-center bg-background">
        <div className="animate-pulse text-muted-foreground">{t('loading')}</div>
      </div>
    );
  }

  if (!hasValidSession) {
    return null; // Will redirect in useEffect
  }

  return (
    <>
      <PublicHeader />
      <div className="min-h-screen-safe relative flex flex-col items-center justify-center bg-background p-4 pt-20">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
              <KeyRound className="h-6 w-6 text-primary" />
            </div>
            <CardTitle className="text-xl">{t('resetPasswordNewTitle')}</CardTitle>
            <CardDescription className="mt-2">
              {t('resetPasswordNewSubtitle')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="password">{t('resetPasswordNewPassword')}</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder={t('authPasswordPlaceholder')}
                    required
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    tabIndex={-1}
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </button>
                </div>
                <p className={`text-xs ${password.length > 0 && !passwordMeetsRequirements ? 'text-destructive' : 'text-muted-foreground'}`}>
                  {t('authPasswordRequirements')}
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword">{t('resetPasswordConfirm')}</Label>
                <div className="relative">
                  <Input
                    id="confirmPassword"
                    type={showConfirmPassword ? "text" : "password"}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder={t('resetPasswordConfirmPlaceholder')}
                    required
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    tabIndex={-1}
                  >
                    {showConfirmPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </button>
                </div>
                {confirmPassword.length > 0 && !passwordsMatch && (
                  <p className="text-xs text-destructive">
                    {t('resetPasswordMismatch')}
                  </p>
                )}
              </div>

              <Button 
                type="submit" 
                className="w-full"
                disabled={isSubmitting || !passwordMeetsRequirements || !passwordsMatch}
              >
                {isSubmitting ? t('authPleaseWait') : t('resetPasswordSubmit')}
              </Button>
            </form>
          </CardContent>
        </Card>
        <p className="absolute bottom-4 text-center text-xs text-muted-foreground">
          {t('footerCopyright').replace('{year}', new Date().getFullYear().toString())}
        </p>
      </div>
    </>
  );
}
